import { useQuery } from '@tanstack/react-query';
import { assembleBalanceSheetDocument } from '@releve/shared/documents/balanceSheet/assemble';
import { supabase } from '@/integrations/supabase/client';
import { normalizeDocumentLanguage } from '@/hooks/useInvoiceDocument';
import { captureException } from '@/lib/sentry';

/**
 * The assembled balance-sheet document, for on-screen rendering of the shared
 * `BalanceSheetDocument` template.
 *
 * Same contract as `useInvoiceDocument`: this is the SAME assembler the PDF
 * endpoint runs (`POST /api/v1/pdf/orgs/:orgId/report`), handed the browser's
 * RLS-scoped client and `inlineLogo: false` because a preview resolves the
 * logo's storage path to a signed URL itself. Screen and PDF therefore print
 * the same numbers by construction, not by convention.
 *
 * `presentationCurrency` is deliberately not passed: the assembler defaults it
 * to the org's base currency, which is exactly what the report endpoint does.
 */
export interface UseBalanceSheetDocumentParams {
  organizationId?: string;
  /** `YYYY-MM-DD` closing date the sheet is drawn at. */
  asOfDate?: string;
  /** Raw `i18n.language`; normalized here so callers don't have to. */
  language: string | undefined;
}

/**
 * Every cached balance sheet of one org (any date, any language) — the prefix
 * a mutation that moves the ledger invalidates.
 */
export const balanceSheetDocumentOrgKey = (organizationId: string | undefined) =>
  ['balance-sheet-document', organizationId] as const;

export const balanceSheetDocumentQueryKey = ({
  organizationId,
  asOfDate,
  language,
}: UseBalanceSheetDocumentParams) =>
  [
    ...balanceSheetDocumentOrgKey(organizationId),
    asOfDate,
    normalizeDocumentLanguage(language),
  ] as const;

export function useBalanceSheetDocument(params: UseBalanceSheetDocumentParams) {
  const { organizationId, asOfDate, language } = params;

  return useQuery({
    queryKey: balanceSheetDocumentQueryKey(params),
    queryFn: async () => {
      if (!organizationId || !asOfDate) return null;
      return assembleBalanceSheetDocument(supabase, {
        orgId: organizationId,
        asOfDate,
        // The preview resolves `org.logo.storagePath` through `useSignedUrl`;
        // only the headless renderer needs the inlined data URI.
        inlineLogo: false,
        language: normalizeDocumentLanguage(language),
        now: new Date(),
        // A failed prior-year column degrades the sheet instead of failing the
        // query — the preview says so, dated, on its face. The query still
        // resolves, so this is the only way the outage is visible to us.
        onComparativeError: (error) =>
          captureException(error, { context: 'useBalanceSheetDocument.comparative' }),
      });
    },
    enabled: !!organizationId && !!asOfDate,
  });
}
